import { Router, watchBusy } from "@frui.ts/screens";
import type { interfaces } from "inversify";
import LocalizationService from "services/localizationService";
import { computed } from "mobx";
import AppRouter from "services/router";
import PayGateRefundPaymentTemplate from "entities/payGateRefundPaymentTemplate";
import type PayGateRefundResponse from "entities/payGateRefundResponse";
import type { EntityNames } from "config";
import EventBus from "services/eventBus";
import { getEntityName } from "utils/entityName";
import AlertsViewModel from "../../../alertsViewModel";
import RefundDetailViewModel from "../detail/refundDetailViewModel";
import * as data from "@frui.ts/data";
import RefundRepository from "repositories/refundRepository";
import OptionsService from "services/optionsService";
import RefundsApprovalService from "services/refundsApprovalService";
import PayGateRefundDetailResponse from "entities/payGateRefundDetailResponse";
import FeeType from "entities/feeType";
import PriceListService from "services/priceListService";
import EnumService from "../../../../services/enumService";
import { emptyValue } from "utils/emptyValue";

@Router.registerRoute({ route: ":refundId", name: Router.Self })
export default class PayGateRefundDetailViewModel extends RefundDetailViewModel<
  PayGateRefundDetailResponse,
  PayGateRefundResponse
> {
  readonly modelName: EntityNames;

  constructor(
    protected itemId: string,
    protected pagingFilter: data.IPagingFilter,
    protected localizationService: LocalizationService,
    protected appRouter: AppRouter,
    protected approvalService: RefundsApprovalService,
    protected optionsService: OptionsService,
    protected refundRepository: RefundRepository,
    protected eventBus: EventBus,
    protected enumService: EnumService,
    protected priceListService: PriceListService,
    public alertsViewModel: AlertsViewModel
  ) {
    super(
      itemId,
      pagingFilter,
      localizationService,
      appRouter,
      approvalService,
      optionsService,
      refundRepository,
      eventBus,
      enumService,
      priceListService,
      alertsViewModel
    );
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.modelName = getEntityName(PayGateRefundDetailResponse)!;
  }

  @watchBusy
  protected async loadDetail(): Promise<PayGateRefundDetailResponse> {
    this.item = await this.refundRepository.getPayGateRefund(this.itemId);

    if (!this.items) {
      this.items = await this.loadList();
    }

    return this.item;
  }

  @watchBusy
  public fetchData() {
    const { limit, offset } = this.pagingFilter;

    return this.refundRepository.getAllPayGateRefunds({ offset, limit });
  }

  @computed
  get chargeRefundFee() {
    return (this.item.fees ?? []).find(fee => fee.feeType === FeeType.ChargeRefundFee)?.amount ?? 0;
  }

  @computed
  get bankFees() {
    return (this.item.fees ?? []).reduce((sum: number, fee) => {
      if (fee.feeType === FeeType.ForeignPaymentCost || fee.feeType === FeeType.ForeignPaymentToSepaCountriesCost) {
        sum += fee.amount;
      }
      return sum;
    }, 0);
  }

  translateRefundReason(refundPaymentTemplateId: string) {
    return (
      this.optionsService.entityOptions(PayGateRefundPaymentTemplate).find(option => option.id === refundPaymentTemplateId)
        ?.label ?? emptyValue
    );
  }

  static Factory({ container }: interfaces.Context) {
    return (refundId: string, pagingFilter: data.IPagingFilter) =>
      new PayGateRefundDetailViewModel(
        refundId,
        pagingFilter,
        container.get(LocalizationService),
        container.get(AppRouter),
        container.get(RefundsApprovalService),
        container.get(OptionsService),
        container.get(RefundRepository),
        container.get(EventBus),
        container.get(EnumService),
        container.get(PriceListService),
        container.get(AlertsViewModel)
      );
  }
}
